/**
 * BoltDisambiguationCard — Renders when entity resolver finds ambiguous names.
 *
 * Shows clickable candidate buttons so the user can pick the correct
 * worker / jobsite before the message is re-sent with a resolved entity ID.
 */

import React from 'react';
import { Users, MapPin } from 'lucide-react';
import './BoltDisambiguationCard.css';

const TYPE_META = {
  worker: { icon: Users, label: 'worker' },
  jobsite: { icon: MapPin, label: 'jobsite' },
};

function CandidateButton({ candidate, type, onSelect }) {
  return (
    <button
      className="bolt-disambig__candidate"
      onClick={() => onSelect(candidate)}
      type="button"
      aria-label={`Select ${candidate.name}`}
    >
      <span className="bolt-disambig__candidate-name">{candidate.name}</span>
      {type === 'worker' && (candidate.role || candidate.trade) && (
        <span className="bolt-disambig__candidate-meta">
          {candidate.role}
          {candidate.role && candidate.trade && ' · '}
          {candidate.trade}
        </span>
      )}
      {type === 'jobsite' && candidate.address && (
        <span className="bolt-disambig__candidate-meta">
          {candidate.address}
        </span>
      )}
    </button>
  );
}

export default function BoltDisambiguationCard({ ambiguities, onResolve }) {
  if (!ambiguities?.length) return null;

  return (
    <div className="bolt-disambig" role="region" aria-label="Disambiguation">
      {ambiguities.map((ambiguity, i) => {
        const meta = TYPE_META[ambiguity.type] || TYPE_META.worker;
        const Icon = meta.icon;

        return (
          <div key={`${ambiguity.type}-${ambiguity.hint}-${i}`} className="bolt-disambig__group">
            <div className="bolt-disambig__header">
              <Icon size={16} aria-hidden="true" />
              <span>
                Which <strong>{meta.label}</strong> did you mean?
                {ambiguity.hint && (
                  <span className="bolt-disambig__hint"> — &quot;{ambiguity.hint}&quot;</span>
                )}
              </span>
            </div>

            <div className="bolt-disambig__candidates">
              {ambiguity.candidates?.map((candidate) => (
                <CandidateButton
                  key={candidate.id}
                  candidate={candidate}
                  type={ambiguity.type}
                  onSelect={(c) => onResolve?.(ambiguity.type, c)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
